import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  DEALER_SELF_ATTEST_CHECKLIST_KEYS,
  groupChecklistByCategory,
  ONBOARDING_LAUNCH_TARGETS,
  resolveOnboardingItemAction,
} from "../../constants/dealerOnboardingActions";
import { trackFoundingDealerEvent } from "../../constants/automapFoundingDealerConstants";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import {
  fetchDealerPortalOnboarding,
  updateDealerPortalChecklistItem,
} from "../../services/automapFoundingDealerApi";

function readinessTone(label) {
  if (label === "launch_ready" || label === "active") return "ready";
  if (label === "needs_inventory" || label === "needs_verification") return "warning";
  if (label === "needs_cleanup") return "danger";
  return "neutral";
}

function metricTone(current, target) {
  if (current >= target) return "good";
  if (current >= target * 0.6) return "warn";
  return "bad";
}

export default function DealerOnboardingPage() {
  const { dealerId, dealerSlug } = useOutletContext();
  const {
    t,
    translateError,
    formatReadinessLabel,
    translateOnboardingChecklistItem,
  } = useDealerPortalIntl();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyKey, setBusyKey] = useState("");

  const load = useCallback(async () => {
    if (!dealerId) {
      setError(t("onboarding.selectDealer"));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await fetchDealerPortalOnboarding(dealerId);
      setPayload(data);
    } catch (err) {
      setError(translateError(err.message, "loadOnboarding"));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [dealerId, t, translateError]);

  useEffect(() => {
    trackFoundingDealerEvent("dealer_onboarding_page_viewed", { dealerId });
    load();
  }, [dealerId, load]);

  const checklistItems = useMemo(() => {
    const items = payload?.checklist?.checklistItems || [];
    return items
      .filter((item) => item.required !== false)
      .map(translateOnboardingChecklistItem);
  }, [payload, translateOnboardingChecklistItem]);

  const groupedSections = useMemo(() => groupChecklistByCategory(checklistItems), [checklistItems]);

  const completedCount = checklistItems.filter((item) => item.status === "completed").length;
  const totalCount = checklistItems.length;
  const progressPct = totalCount ? Math.round((completedCount / totalCount) * 100) : 0;

  const firstOpenItem = useMemo(
    () => checklistItems.find((item) => item.status !== "completed"),
    [checklistItems]
  );

  const metrics = payload?.metrics || {};
  const readinessScore = payload?.readinessScore ?? 0;

  const handleSelfAttest = async (itemKey) => {
    setBusyKey(itemKey);
    setError("");
    try {
      const data = await updateDealerPortalChecklistItem(dealerId, itemKey, { status: "completed" });
      setPayload(data);
      trackFoundingDealerEvent("dealer_onboarding_checklist_completed", { dealerId, itemKey });
    } catch (err) {
      setError(translateError(err.message, "updateOnboarding"));
    } finally {
      setBusyKey("");
    }
  };

  const renderItemHint = (item) => {
    const hintKey = `onboarding.hints.${item.key}`;
    const hint = t(hintKey);
    return hint && hint !== hintKey ? hint : null;
  };

  const renderItemMetric = (item) => {
    if (item.key === "min_ten_active_listings") {
      return t("onboarding.metricActiveListings", {
        current: metrics.activeCount ?? 0,
        target: ONBOARDING_LAUNCH_TARGETS.activeListings,
      });
    }
    if (item.key === "listings_have_photos") {
      return t("onboarding.metricPhotoCoverage", {
        current: metrics.photoCoveragePct ?? 0,
        target: ONBOARDING_LAUNCH_TARGETS.photoCoveragePct,
      });
    }
    if (item.key === "listings_confirmed_available") {
      return t("onboarding.metricFreshness", {
        current: metrics.freshnessPct ?? 0,
        target: ONBOARDING_LAUNCH_TARGETS.freshnessPct,
      });
    }
    return null;
  };

  if (loading) {
    return (
      <div className="dealer-onboarding">
        <div className="dealer-onboarding__hero dealer-onboarding__hero--skeleton" aria-busy="true">
          <div className="dealer-onboarding__skeleton-line dealer-onboarding__skeleton-line--title" />
          <div className="dealer-onboarding__skeleton-line" />
          <div className="dealer-onboarding__skeleton-progress" />
        </div>
        <p className="dealer-onboarding__status">{t("onboarding.loading")}</p>
      </div>
    );
  }

  return (
    <div className="dealer-onboarding">
      <header className="dealer-onboarding__hero">
        <div className="dealer-onboarding__hero-copy">
          <p className="dealer-onboarding__eyebrow">{t("onboarding.eyebrow")}</p>
          <h1>{t("onboarding.title")}</h1>
          <p className="dealer-onboarding__subtitle">{t("onboarding.subtitle")}</p>
        </div>

        <div className="dealer-onboarding__score-card">
          <div
            className="dealer-onboarding__score-ring"
            style={{ "--progress": `${readinessScore}%` }}
            aria-hidden="true"
          >
            <span>{readinessScore}</span>
          </div>
          <div className="dealer-onboarding__score-meta">
            <span className="dealer-onboarding__score-label">{t("onboarding.progress")}</span>
            <span className={`dealer-onboarding__status-pill dealer-onboarding__status-pill--${readinessTone(payload?.readinessLabel)}`}>
              {formatReadinessLabel(payload?.readinessLabel)}
            </span>
            <p className="dealer-onboarding__steps-summary">
              {t("onboarding.stepsComplete", { completed: completedCount, total: totalCount })}
            </p>
          </div>
        </div>

        <div className="dealer-onboarding__progress-track" aria-hidden="true">
          <div className="dealer-onboarding__progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <p className="dealer-onboarding__progress-caption">
          {t("onboarding.checklistProgress", { percent: progressPct })}
        </p>
      </header>

      {error ? <p className="dealer-state-box dealer-onboarding__message">{error}</p> : null}

      {payload?.launchReady ? (
        <section className="dealer-onboarding__banner dealer-onboarding__banner--ready">
          <strong>{t("onboarding.launchReadyTitle")}</strong>
          <p>{t("onboarding.launchReady")}</p>
        </section>
      ) : firstOpenItem ? (
        <section className="dealer-onboarding__banner dealer-onboarding__banner--next">
          <div>
            <span className="dealer-onboarding__banner-label">{t("onboarding.nextUp")}</span>
            <strong>{firstOpenItem.title}</strong>
            {renderItemHint(firstOpenItem) ? <p>{renderItemHint(firstOpenItem)}</p> : null}
          </div>
          {(() => {
            const action = resolveOnboardingItemAction(firstOpenItem.key, { dealerSlug });
            if (action.adminOnly) return null;
            return (
              <Link
                className="dealer-portal-btn primary"
                to={action.to}
                target={action.external ? "_blank" : undefined}
                rel={action.external ? "noreferrer" : undefined}
                onClick={() =>
                  trackFoundingDealerEvent("dealer_onboarding_cta_clicked", { cta: firstOpenItem.key })
                }
              >
                {t(`onboarding.actions.${action.labelKey}`)}
              </Link>
            );
          })()}
        </section>
      ) : null}

      <section className="dealer-onboarding__metrics" aria-label={t("onboarding.metricsTitle")}>
        <article className={`dealer-onboarding__metric dealer-onboarding__metric--${metricTone(metrics.activeCount ?? 0, ONBOARDING_LAUNCH_TARGETS.activeListings)}`}>
          <span>{t("onboarding.activeListings")}</span>
          <strong>
            {metrics.activeCount ?? 0}
            <small> / {ONBOARDING_LAUNCH_TARGETS.activeListings}</small>
          </strong>
        </article>
        <article className={`dealer-onboarding__metric dealer-onboarding__metric--${metricTone(metrics.photoCoveragePct ?? 0, ONBOARDING_LAUNCH_TARGETS.photoCoveragePct)}`}>
          <span>{t("onboarding.photoCoverage")}</span>
          <strong>{metrics.photoCoveragePct ?? 0}%</strong>
        </article>
        <article className={`dealer-onboarding__metric dealer-onboarding__metric--${metricTone(metrics.freshnessPct ?? 0, ONBOARDING_LAUNCH_TARGETS.freshnessPct)}`}>
          <span>{t("onboarding.freshness")}</span>
          <strong>{metrics.freshnessPct ?? 0}%</strong>
        </article>
        <article className="dealer-onboarding__metric dealer-onboarding__metric--neutral">
          <span>{t("onboarding.launchTarget")}</span>
          <strong>{ONBOARDING_LAUNCH_TARGETS.readinessScore}/100</strong>
        </article>
      </section>

      <div className="dealer-onboarding__sections">
        {groupedSections.map(({ category, items }) => {
          const sectionComplete = items.filter((item) => item.status === "completed").length;
          return (
            <section key={category} className="dealer-onboarding__section">
              <header className="dealer-onboarding__section-header">
                <div>
                  <h2>{t(`onboarding.categories.${category}`)}</h2>
                  <p className="muted">
                    {t("onboarding.sectionProgress", { completed: sectionComplete, total: items.length })}
                  </p>
                </div>
              </header>

              <ol className="dealer-onboarding__steps">
                {items.map((item, index) => {
                  const complete = item.status === "completed";
                  const action = resolveOnboardingItemAction(item.key, { dealerSlug });
                  const hint = renderItemHint(item);
                  const metric = renderItemMetric(item);
                  const canSelfAttest =
                    !complete && DEALER_SELF_ATTEST_CHECKLIST_KEYS.has(item.key) && action.selfAttest;

                  return (
                    <li
                      key={item.key}
                      className={`dealer-onboarding__step${complete ? " is-complete" : ""}`}
                    >
                      <div className="dealer-onboarding__step-index" aria-hidden="true">
                        {complete ? "✓" : index + 1}
                      </div>
                      <div className="dealer-onboarding__step-body">
                        <div className="dealer-onboarding__step-head">
                          <h3>{item.title}</h3>
                          <span className={`dealer-onboarding__step-status${complete ? " is-complete" : ""}`}>
                            {complete ? t("onboarding.stepComplete") : t("onboarding.stepPending")}
                          </span>
                        </div>
                        {hint ? <p className="dealer-onboarding__step-hint">{hint}</p> : null}
                        {metric ? <p className="dealer-onboarding__step-metric">{metric}</p> : null}

                        {!complete && !action.adminOnly ? (
                          <div className="dealer-onboarding__step-actions">
                            <Link
                              className="dealer-portal-btn primary"
                              to={action.to}
                              target={action.external ? "_blank" : undefined}
                              rel={action.external ? "noreferrer" : undefined}
                              onClick={() =>
                                trackFoundingDealerEvent("dealer_onboarding_cta_clicked", { cta: item.key })
                              }
                            >
                              {t(`onboarding.actions.${action.labelKey}`)}
                            </Link>
                            {canSelfAttest ? (
                              <button
                                type="button"
                                className="dealer-portal-btn"
                                disabled={busyKey === item.key}
                                onClick={() => handleSelfAttest(item.key)}
                              >
                                {busyKey === item.key
                                  ? t("onboarding.markingComplete")
                                  : t("onboarding.markComplete")}
                              </button>
                            ) : null}
                          </div>
                        ) : null}

                        {!complete && action.adminOnly ? (
                          <p className="dealer-onboarding__step-note">{t("onboarding.adminReviewNote")}</p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          );
        })}
      </div>

      <section className="dealer-onboarding__help">
        <h2>{t("onboarding.helpTitle")}</h2>
        <p className="muted">{t("onboarding.helpBody")}</p>
        <div className="dealer-onboarding__help-actions">
          <Link className="dealer-portal-btn" to="/v/automap/dealer/inventario/importar">
            {t("onboarding.ctaUploadInventory")}
          </Link>
          <Link className="dealer-portal-btn" to="/v/automap/dealer/plan">
            {t("plan.contactAutoMapa")}
          </Link>
        </div>
      </section>
    </div>
  );
}
